import { AUTH_STORE_PERSIST_KEY, type AuthTokenPair } from './tokens';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

type LocalStorageLike = {
  getItem: (key: string) => string | null;
};

function getLocalStorage(): LocalStorageLike | undefined {
  const candidate = (globalThis as Record<string, unknown>).localStorage;
  if (!isRecord(candidate) || typeof candidate.getItem !== 'function') {
    return undefined;
  }
  return candidate as LocalStorageLike;
}

/**
 * Synchronously reads auth tokens from Zustand persist (localStorage).
 * Used to pick up a refresh performed in another browser tab before treating
 * a refresh 401 as session death.
 */
export function readPersistedAuthTokens(): AuthTokenPair | undefined {
  const storage = getLocalStorage();
  if (!storage) {
    return undefined;
  }

  try {
    const raw = storage.getItem(AUTH_STORE_PERSIST_KEY);
    if (!raw) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return undefined;
    }

    const state = parsed.state;
    if (!isRecord(state)) {
      return undefined;
    }

    const tokens = state.tokens;
    if (!isRecord(tokens)) {
      return undefined;
    }

    const accessToken = tokens.accessToken;
    const refreshToken = tokens.refreshToken;
    if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
      return undefined;
    }
    if (!accessToken || !refreshToken) {
      return undefined;
    }

    return { accessToken, refreshToken };
  } catch {
    return undefined;
  }
}
